import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { ArrowRight, Heart, Quote, ShieldCheck, TrendingUp } from "lucide-react";

export default function FounderPage() {
  return (
    <div data-testid="founder-page" className="bg-white">
      <Seo
        title="Founder's Story — From Loss to Mission"
        description="DealVest was not created in a boardroom. It was created from real entrepreneurial experience — 20+ years, 24+ companies, 700+ employees, and a journey of losing and rebuilding. Read the founder's story."
        path="/founder"
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "DealVest Founder's Story",
          "description": "Personal story of DealVest's founder — building, losing, and rebuilding businesses across Africa.",
          "publisher": { "@type": "Organization", "name": "DealVest" }
        }}
      />

      {/* HERO */}
      <section className="relative pt-12 md:pt-20 pb-10 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-blue-100/50 blur-3xl" />
        </div>
        <div className="max-w-[820px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="dv-pill mb-5"><Heart size={13} className="text-rose-500" /> Founder's Story</span>
          <h1 className="dv-display text-4xl md:text-6xl mb-5 leading-[1.05]">
            DealVest was not created<br /><span className="text-blue-600">in a boardroom.</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            It was created from the personal experience of a founder who spent more than 20 years building businesses across Africa and beyond.
          </p>
        </div>
      </section>

      {/* THE PEAK */}
      <section className="max-w-[820px] mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-3 gap-4 mb-10">
          {[
            { v: "24+", l: "Companies" },
            { v: "700+", l: "Employees" },
            { v: "$M+", l: "Annual revenue" },
          ].map((s, i) => (
            <div key={i} className="text-center p-5 bg-blue-50 rounded-2xl">
              <div className="text-3xl md:text-5xl font-bold text-blue-600">{s.v}</div>
              <div className="text-xs md:text-sm text-slate-600 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="prose-lg space-y-6 text-slate-700 text-[17px] leading-relaxed">
          <p>At one point, he had built a thriving group of businesses — 24 companies, more than 700 employees, millions of dollars in annual revenue, and a growing business empire across multiple sectors.</p>
          <p className="text-slate-900 font-semibold">Then a major government policy change affected the core business that funded and sustained the rest of the group.</p>
          <p>Cash flow stopped.</p>
          <p>The businesses were not failing because they were bad businesses. They were failing because they could no longer access the funding, refinancing, investment, acquisition opportunities, or strategic partnerships needed to survive and grow.</p>
          <p className="border-l-4 border-blue-600 pl-6 italic text-slate-800">One by one, businesses that had taken years to build had to be sold, liquidated, or shut down. An entire business empire was lost.</p>
        </div>
      </section>

      {/* THE REALIZATION */}
      <section className="bg-slate-50 py-12 md:py-20">
        <div className="max-w-[820px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="dv-eyebrow">The realization</span>
          <h2 className="dv-display text-3xl md:text-5xl my-5 leading-tight">
            Many businesses do not fail<br /><span className="text-blue-600">because they are bad businesses.</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            They fail because they run out of capital, liquidity, buyers, investors, or strategic support at critical moments. That realization became the foundation of DealVest.
          </p>
          <div className="mt-10 p-8 md:p-10 bg-blue-600 text-white rounded-3xl max-w-2xl mx-auto">
            <Quote size={28} className="text-blue-200 mb-3 mx-auto" />
            <p className="dv-display text-2xl md:text-3xl leading-snug">
              "Most great businesses need capital before they become great businesses."
            </p>
          </div>
        </div>
      </section>

      {/* LETTER FROM THE FOUNDER */}
      <section className="max-w-[760px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="text-center mb-10">
          <span className="dv-eyebrow">A Letter From The Founder</span>
          <h2 className="dv-display text-3xl md:text-5xl mt-3">For every entrepreneur still building.</h2>
        </div>
        <div className="space-y-5 text-slate-700 text-[17px] leading-[1.75]">
          <p>If you are reading this, you are probably building something.</p>
          <p>Maybe a small farm. Maybe a software startup. Maybe a retail shop that's been in your family for years. Maybe just an idea you can't stop thinking about.</p>
          <p>I have built businesses for over two decades. I have employed hundreds of people. I have had a multi-company group throwing off millions in revenue.</p>
          <p>And I have lost it all.</p>
          <p>Not because the businesses were bad. They weren't. They were good businesses run by good people serving real customers. They failed because a single policy change cut off the capital that kept them alive, and at that moment, there was nowhere to turn — no platform, no marketplace, no easy way to find investors, buyers, or partners who could step in.</p>
          <p>By the time I found the right people, it was too late.</p>
          <p className="text-slate-900 font-semibold">I built DealVest so no other founder has to walk that road alone.</p>
          <p>Whether you want to raise capital, sell what you've built, buy something new, find a strategic partner, or just understand what your business or idea is really worth — DealVest is here to make those connections faster, easier, and accessible from your phone.</p>
          <p>I built it from scratch, in pain, with hope. I hope it serves you the way I wish it had served me.</p>
          <p>Keep building.</p>
          <p className="italic text-slate-600">— The Founder, DealVest</p>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, title: "Built by entrepreneurs", desc: "Created from real business experience — wins, losses, and lessons." },
            { icon: TrendingUp, title: "Designed for survival & scale", desc: "Help businesses raise capital, find buyers, or pivot before it's too late." },
            { icon: Heart, title: "Africa & Middle East first", desc: "Built for the markets we live in, in the languages we speak." },
          ].map((b, i) => (
            <div key={i} className="dv-card p-7">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4"><b.icon size={22} className="text-blue-600" /></div>
              <h3 className="font-bold text-lg mb-2">{b.title}</h3>
              <p className="text-slate-600 text-[15px] leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 p-10 md:p-14 text-white text-center">
          <h2 className="dv-display text-3xl md:text-4xl mb-4">Don't let great businesses die quietly.</h2>
          <p className="text-blue-100 text-lg max-w-xl mx-auto mb-7">Whether you're at the peak or rebuilding — connect with the people who can help.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup" className="dv-btn bg-white text-blue-600 hover:bg-blue-50" data-testid="founder-signup-cta">Get started free <ArrowRight size={16} /></Link>
            <Link to="/marketplace" className="dv-btn bg-white/10 hover:bg-white/20 text-white border border-white/25">Browse marketplace</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
